import { faChevronRight, faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { School } from '@propertypal/shared/src/types/schools';
import { getCurrentSchoolYear } from '@propertypal/shared/src/utils/schools';
import Link from 'next/link';
import React, { FunctionComponent, MouseEvent, useRef, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import Chevron from '../icons/Chevron';
import { closeTooltip, openTooltip } from '../notifications/Tooltip';
import { LinkText, Text } from '../typography';
import InfoBox from './InfoBox';
import InfoIcon from './InfoIcon';
import {
  Cell,
  Column,
  Container,
  Header,
  LoadingBox,
  SchoolName,
  SchoolNames,
  SlideButton,
} from './SchoolsTable.style';

interface Props {
  schools?: School[];
  isROI?: boolean;
  error?: Error;
  loading: boolean;
  errorText: string;
}

interface InfoBoxParams {
  type: string;
  school: School;
  target: EventTarget;
}

const BREAK_POINTS = {
  890: {
    slidesPerView: 4,
  },
  660: {
    slidesPerView: 3,
  },
  400: {
    slidesPerView: 2,
  },
  0: {
    slidesPerView: 1,
  },
};

const SchoolsTable: FunctionComponent<Props> = (props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [swiper, setSwiper] = useState<any>(null);
  const [isStart, setStart] = useState<boolean>(true);
  const [isEnd, setEnd] = useState<boolean>(false);
  const [activeInfoBox, setActiveInfoBox] = useState<InfoBoxParams | null>(null);
  const currentYear = getCurrentSchoolYear(props.schools);

  const onSlideChange = (mSwiper: any) => {
    setActiveInfoBox(null);
    setStart(mSwiper.isBeginning);
    setEnd(mSwiper.isEnd);
  };

  const openInfoBox = (type: string, school: School) => (e: MouseEvent) => {
    e.stopPropagation();
    closeTooltip();
    setActiveInfoBox({
      type,
      school,
      target: e.target,
    });
  };

  if (props.loading) {
    return (
      <LoadingBox>
        <Loader color={theme.backgroundLight} />
      </LoadingBox>
    );
  }

  if (props.error || !props.schools) {
    return (
      <div>
        <Text align="center" mt={30} mb={30}>
          {props.errorText}
        </Text>
      </div>
    );
  }

  return (
    <>
      <Header>
        <SlideButton
          aria-label="School data last slide"
          className="pp-schools-slide-button"
          data-testid="schools-prev-slide"
          onClick={() => swiper?.slidePrev()}
          hidden={isStart}
        >
          <FontAwesomeIcon icon={faChevronLeft} color="white" style={{ fontSize: 24 }} />
        </SlideButton>

        <SlideButton
          aria-label="School data next slide"
          className="pp-schools-slide-button"
          data-testid="schools-next-slide"
          onClick={() => swiper?.slideNext()}
          disabled={isEnd}
        >
          <FontAwesomeIcon icon={faChevronRight} color="white" style={{ fontSize: 24 }} />
        </SlideButton>
      </Header>

      {activeInfoBox && (
        <InfoBox
          type={activeInfoBox.type}
          school={activeInfoBox.school}
          onClose={() => setActiveInfoBox(null)}
          target={activeInfoBox.target as HTMLElement}
        />
      )}

      <Container
        ref={contentRef}
        data-testid="school-table-columns"
        onClick={() => {
          setActiveInfoBox(null);
          closeTooltip();
        }}
      >
        <SchoolNames>
          <SchoolName>
            <p>SCHOOL NAME</p>
          </SchoolName>

          {props.schools.map((school) => (
            <SchoolName key={`${school.id}`} data-testid="school-data-row">
              <p>{school.name}</p>
            </SchoolName>
          ))}
        </SchoolNames>

        <Swiper
          onSwiper={setSwiper}
          slidesPerView={4}
          breakpoints={BREAK_POINTS}
          simulateTouch
          threshold={5}
          onSlideChangeTransitionEnd={onSlideChange}
          onTouchMove={() => {
            closeTooltip();
            setActiveInfoBox(null);
          }}
        >
          {props.schools[0].distanceText && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>DISTANCE FROM PROPERTY</p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`distance${school.id}`}>
                    <p>{school.distanceText}</p>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {!props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>
                    APPROVED ADMISSIONS (MAX) {currentYear}
                    <InfoIcon
                      onClick={(e) =>
                        openTooltip('The number of pupils a school can admit to its first year group.', e)
                      }
                    />
                  </p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`years${school.id}`}>
                    <p>{school.years[0].numberOfAdmissions}</p>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {!props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>
                    APPLICATIONS (TOTAL) {currentYear}
                    <InfoIcon onClick={(e) => openTooltip('The total number of applications the school received', e)} />
                  </p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`applications${school.id}`}>
                    {!!school.years[0].totalApplications && (
                      <button onClick={openInfoBox('applications', school)} type="button">
                        <p>
                          {school.years[0].totalApplications} <br />{' '}
                          <LinkText>
                            View previous <Chevron size={10} />
                          </LinkText>
                        </p>
                      </button>
                    )}

                    {!school.years[0].totalApplications && <p>Unavailable</p>}
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {!props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>
                    ADMISSIONS (TOTAL) {currentYear}
                    <InfoIcon
                      onClick={(e) =>
                        openTooltip('The number of pupils admitted by the school (incl. special circumstances).', e)
                      }
                    />
                  </p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`admitted${school.id}`}>
                    {!!school.years[0].totalApplications && (
                      <button onClick={openInfoBox('admissions', school)} type="button">
                        <p>
                          {school.years[0].totalAdmissions} <br />{' '}
                          <LinkText>
                            View previous <Chevron size={10} />
                          </LinkText>
                        </p>
                      </button>
                    )}

                    {!school.years[0].totalApplications && <p>Unavailable</p>}
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {!props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>
                    APPROVED ENROLMENT (MAX) {currentYear}
                    <InfoIcon
                      onClick={(e) =>
                        openTooltip(
                          'The max number of pupils the school can have enrolled (all year groups), approved by the Education Authority.',
                          e,
                        )
                      }
                    />
                  </p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`approved${school.id}`}>
                    <p>{school.years[0].numberOfPupils}</p>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          <SwiperSlide>
            <Column>
              <Cell>
                <p>SCHOOL DETAILS</p>
              </Cell>

              {props.schools.map((school) => (
                <Cell key={`details${school.id}`}>
                  <button onClick={openInfoBox('details', school)} type="button" data-testid="view-school-details">
                    <p>
                      <LinkText>
                        View details <Chevron size={10} />
                      </LinkText>
                    </p>
                  </button>
                </Cell>
              ))}
            </Column>
          </SwiperSlide>

          {!props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>ADMISSIONS CRITERIA {currentYear}</p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`criteria${school.id}`}>
                    <p>
                      <LinkText href={school.years[0].admissionsPDF} target="_blank" rel="noreferrer">
                        View criteria <Chevron size={10} />
                      </LinkText>
                    </p>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>TOTAL PUPILS ({currentYear})</p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`totalPupils${school.id}`}>
                    <button onClick={openInfoBox('totalPupils', school)} type="button">
                      <p>
                        {school.years[0].totalPupilsROI} <br />
                        <LinkText>View previous years</LinkText>
                      </p>
                    </button>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>ETHOS</p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`ethos${school.id}`}>
                    <p>{school.ethosText}</p>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {props.isROI && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>GENDER</p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`gender${school.id}`}>
                    <p>{school.genderText}</p>
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}

          {props.schools[0].search && (
            <SwiperSlide>
              <Column>
                <Cell>
                  <p>PROPERTIES NEARBY</p>
                </Cell>

                {props.schools.map((school) => (
                  <Cell key={`nearby${school.id}`}>
                    {school.search && (
                      <p>
                        <Link href={school.search.saleUrl} passHref>
                          <LinkText>
                            For Sale <Chevron size={10} />
                          </LinkText>
                        </Link>
                        <br />
                        <Link href={school.search.rentUrl} passHref>
                          <LinkText>
                            For Rent <Chevron size={10} />
                          </LinkText>
                        </Link>
                      </p>
                    )}
                  </Cell>
                ))}
              </Column>
            </SwiperSlide>
          )}
        </Swiper>
      </Container>
    </>
  );
};

export default SchoolsTable;
